import { useMemo } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import type { NavigateFunction } from "react-router-dom";

// ── Link adapter ──────────────────────────────────────────────────────────
// AppSidebar never talks to the router directly: every `to` in a NavModel goes
// through an adapter, so the consultant read-through can mount the client nav
// under /consultant/clients/:orgId/portal without touching the model.

export interface NavigateOptions {
  replace?: boolean;
}

export interface LinkAdapter {
  /** Absolute app path for an <a href>. */
  href(to: string): string;
  navigate(to: string, options?: NavigateOptions): void;
  /**
   * NavLink-style matching: exact match, or a prefix match at a segment
   * boundary. `activeMatch` lists extra prefixes that also count as active;
   * `exact` restricts `to` itself to an exact match (NavItem.exact).
   */
  isActive(to: string, activeMatch?: string[], exact?: boolean): boolean;
}

/** Joins a prefix and a shell-relative path, keeping any query string. */
export function joinPrefix(prefix: string, to: string): string {
  const base = prefix.replace(/\/+$/, "");
  if (!base) return to;
  if (to === "" || to === "/") return base;
  return base + (to.startsWith("/") ? to : `/${to}`);
}

function pathnameOf(to: string): string {
  const cut = to.search(/[?#]/);
  return cut === -1 ? to : to.slice(0, cut);
}

/**
 * True when `pathname` is `pattern` or, unless `exact`, sits below it. Root
 * ("/") only matches exactly, as NavLink does.
 */
export function isPathActive(pattern: string, pathname: string, exact = false): boolean {
  const target = pathnameOf(pattern).replace(/\/+$/, "") || "/";
  if (target === "/") return pathname === "/";
  return matchPath({ path: target, end: exact }, pathname) !== null;
}

export interface RouterAdapterInput {
  navigate: NavigateFunction;
  pathname: string;
  /** Prefix for every `to` (the read-through). Empty for the root shells. */
  prefix?: string;
}

export function createRouterAdapter({ navigate, pathname, prefix = "" }: RouterAdapterInput): LinkAdapter {
  const href = (to: string) => joinPrefix(prefix, to);
  return {
    href,
    navigate: (to, options) => navigate(href(to), options?.replace ? { replace: true } : undefined),
    isActive: (to, activeMatch, exact) => {
      if (isPathActive(href(to), pathname, exact)) return true;
      return (activeMatch ?? []).some((extra) => isPathActive(href(extra), pathname));
    },
  };
}

/** The default adapter: react-router at the root. */
export function useRouterLinkAdapter(): LinkAdapter {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return useMemo(() => createRouterAdapter({ navigate, pathname }), [navigate, pathname]);
}

export interface PrefixAdapterOptions {
  /**
   * Paths that must not be prefixed (absolute app routes that a prefixed
   * model still links to). Defaults to the consultant and auditor shells.
   */
  passthrough?: (to: string) => boolean;
}

const DEFAULT_PASSTHROUGH = (to: string) => to.startsWith("/consultant/") || to.startsWith("/auditor/") || to === "/consultant" || to === "/auditor";

/**
 * Factory for the read-through: wraps a base adapter so client paths become
 * `${prefix}${to}`. Apply it to the router adapter:
 *
 *   const adapter = prefixClientAdapter(`/consultant/clients/${orgId}/portal`)(useRouterLinkAdapter());
 */
export function prefixClientAdapter(prefix: string, options: PrefixAdapterOptions = {}): (base: LinkAdapter) => LinkAdapter {
  const passthrough = options.passthrough ?? DEFAULT_PASSTHROUGH;
  const resolve = (to: string) => (passthrough(to) ? to : joinPrefix(prefix, to));
  return (base) => ({
    href: (to) => base.href(resolve(to)),
    navigate: (to, opts) => base.navigate(resolve(to), opts),
    isActive: (to, activeMatch, exact) => base.isActive(resolve(to), activeMatch?.map(resolve), exact),
  });
}

/** Hook form of prefixClientAdapter on top of the react-router adapter. */
export function usePrefixedClientAdapter(prefix: string, options?: PrefixAdapterOptions): LinkAdapter {
  const base = useRouterLinkAdapter();
  const passthrough = options?.passthrough;
  return useMemo(() => prefixClientAdapter(prefix, { passthrough })(base), [base, prefix, passthrough]);
}

/** Detects the plain left click a link should intercept (no modifier, no middle button). */
export function isPlainLeftClick(e: { button: number; metaKey: boolean; ctrlKey: boolean; shiftKey: boolean; altKey: boolean; defaultPrevented: boolean }): boolean {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && !e.defaultPrevented;
}
